import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, BellRing, Timer, BadgeDollarSign } from "lucide-react";
import api from "../lib/api";
import DeadlineTracker from "../components/DeadlineTracker";
import LeadForm from "../components/LeadForm";
import CategoryIcon from "../components/CategoryIcon";
import HeroSlider from "../components/HeroSlider";
import Ticker from "../components/Ticker";
import Reveal from "../components/Reveal";
import TestimonialCarousel from "../components/TestimonialCarousel";

const WHY_ICONS = [ShieldCheck, BellRing, Timer, BadgeDollarSign];

export default function Home() {
  const [categories, setCategories] = useState([]);
  const [content, setContent] = useState(null);
  const [testimonials, setTestimonials] = useState([]);
  const [posts, setPosts] = useState([]);

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
    api.get("/content").then((r) => { setContent(r.data.content); setTestimonials(r.data.testimonials); }).catch(() => {});
    api.get("/blog").then((r) => setPosts(r.data.slice(0, 3))).catch(() => {});
  }, []);

  const why = content?.why_choose_us || [];

  return (
    <div data-testid="home-page">
      {/* HERO SLIDER */}
      <HeroSlider content={content} />
      <Ticker />

      {/* SERVICE CATEGORIES */}
      <section className="py-20 lg:py-28 bg-[#0f1830]" data-testid="services-overview-section">
        <Reveal className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-12">
            <div className="max-w-2xl">
              <p className="font-mono2 text-xs uppercase tracking-widest text-[#F97316] mb-3">What We Do</p>
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-white tracking-tight leading-none">
                Every Filing Your Fleet Needs
              </h2>
              <p className="text-slate-400 mt-4 text-sm sm:text-base leading-relaxed">
                50+ services organized into clear categories. Pick your lane — we handle the paperwork.
              </p>
            </div>
            <Link to="/services" data-testid="view-all-services-btn"
              className="text-sm font-semibold text-[#F97316] hover:text-[#EA580C] flex items-center gap-1.5 shrink-0">
              View all services <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {categories.map((cat, i) => (
              <Link key={cat.id} to={`/services#${cat.slug}`} data-testid={`category-card-${cat.slug}`}
                className="group bg-[#131C38] border border-[#1E293B] rounded-lg p-6 hover:border-[#F97316]/60 hover:translate-y-[-4px] transition-all duration-300"
                style={{ animationDelay: `${i * 0.05}s` }}>
                <div className="w-12 h-12 rounded-md bg-[#F97316]/10 border border-[#F97316]/25 flex items-center justify-center text-[#F97316] mb-5 group-hover:bg-[#F97316] group-hover:text-[#0B132B] transition-colors duration-300">
                  <CategoryIcon icon={cat.icon} />
                </div>
                <h3 className="font-display text-xl font-semibold text-white mb-2">{cat.name}</h3>
                <p className="text-xs text-slate-400 leading-relaxed mb-4">{cat.description}</p>
                <span className="font-mono2 text-[11px] text-slate-500 uppercase tracking-wider">
                  {cat.services?.length || 0} services →
                </span>
              </Link>
            ))}
          </div>
        </Reveal>
      </section>

      <DeadlineTracker />

      {/* WHY CHOOSE US */}
      {why.length > 0 && (
        <section className="py-20 lg:py-28 bg-[#0f1830]" data-testid="why-choose-us-section">
          <Reveal className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-2xl mb-12">
              <p className="font-mono2 text-xs uppercase tracking-widest text-[#F97316] mb-3">Why Crystal</p>
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-white tracking-tight leading-none">
                Why Choose Crystal Logistics Services
              </h2>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {why.map((w, i) => {
                const Icon = WHY_ICONS[i % WHY_ICONS.length];
                return (
                  <div key={i} data-testid={`why-card-${i}`}
                    className="bg-[#131C38] border border-[#1E293B] rounded-lg p-7 hover:border-[#F97316]/50 transition-colors duration-300">
                    <Icon className="w-8 h-8 text-[#F97316] mb-5" />
                    <h3 className="font-display text-xl font-semibold text-white mb-2">{w.title}</h3>
                    <p className="text-sm text-slate-400 leading-relaxed">{w.text}</p>
                  </div>
                );
              })}
            </div>
          </Reveal>
        </section>
      )}

      {/* TESTIMONIALS */}
      <TestimonialCarousel testimonials={testimonials} />

      {/* BLOG PREVIEW */}
      {posts.length > 0 && (
        <section className="py-20 lg:py-28 bg-[#0f1830]" data-testid="blog-preview-section">
          <Reveal className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-12">
              <div>
                <p className="font-mono2 text-xs uppercase tracking-widest text-[#F97316] mb-3">Trucking Insights</p>
                <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-white tracking-tight leading-none">
                  From the Road Desk
                </h2>
              </div>
              <Link to="/blog" data-testid="view-all-blog-btn"
                className="text-sm font-semibold text-[#F97316] hover:text-[#EA580C] flex items-center gap-1.5 shrink-0">
                All articles <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="grid md:grid-cols-3 gap-5">
              {posts.map((p) => (
                <Link key={p.id} to={`/blog/${p.slug}`} data-testid={`blog-preview-${p.slug}`}
                  className="group bg-[#131C38] border border-[#1E293B] rounded-lg overflow-hidden hover:border-[#F97316]/60 hover:translate-y-[-4px] transition-all duration-300">
                  <div className="h-44 overflow-hidden">
                    <img src={p.cover_image} alt={p.title} loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  </div>
                  <div className="p-6">
                    <h3 className="font-display text-lg font-semibold text-white leading-snug mb-2 group-hover:text-[#F97316] transition-colors">{p.title}</h3>
                    <p className="text-xs text-slate-400 leading-relaxed line-clamp-2">{p.excerpt}</p>
                  </div>
                </Link>
              ))}
            </div>
          </Reveal>
        </section>
      )}

      {/* LEAD FORM CTA */}
      <section className="py-20 lg:py-28 bg-[#0B132B] relative grain-overlay" data-testid="lead-form-section">
        <Reveal className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-14 items-center">
          <div>
            <p className="font-mono2 text-xs uppercase tracking-widest text-[#F97316] mb-3">Free Consultation</p>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-white tracking-tight leading-none mb-5">
              Tell Us What You're Hauling. We'll Handle the Rest.
            </h2>
            <p className="text-slate-400 text-sm sm:text-base leading-relaxed mb-8 max-w-md">
              No chatbots, no WhatsApp runaround. Leave your details and a real permit specialist calls you back within 1–2 business hours.
            </p>
            <ul className="space-y-3 text-sm text-slate-300">
              {["Nationwide coverage — all 50 states", "Flat, quoted pricing before we start", "Deadline tracking included for every client"].map((li) => (
                <li key={li} className="flex items-center gap-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#F97316]" /> {li}
                </li>
              ))}
            </ul>
          </div>
          <div className="bg-[#131C38] border border-[#1E293B] rounded-lg p-7 sm:p-9 animate-glow">
            <LeadForm />
          </div>
        </Reveal>
      </section>
    </div>
  );
}
